import React, { Fragment, useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';
import { Button, ButtonToolbar, Card, CardBody, Col, Row, Badge } from 'reactstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector, reset } from 'redux-form';
import {
  FLOWS_SUCCESS,
  getFlowsList as getFlowsListAction,
  flowClearState as flowClearStateAction,
  flowClearFlashMessage as flowClearFlashMessageAction,
} from '../../../../redux/actions/flowsAction';
import renderField from '../../../../shared/components/form/TextField';
import renderSelectField from '../../../../shared/components/form/Select';
import DataTable from '../../../../shared/components/data-table';
import { COMPLETED, WAIT } from '../../../../shared/components/data-table/submitStatuses';
import { DATA, ACTION } from '../../../../shared/components/data-table/columnTypes';
import { LINK, OPEN_WINDOW } from '../../../../shared/components/data-table/buttonTypes';
import { FullWideNotification } from '../../../../shared/components/Notification';
import store from '../../../App/store';
import config from '../../../../config';

const FlowTable = ({
  getFlowsList,
  flowClearState,
  flowClearFlashMessage,
  showNotification,
  flowsList,
  pagination,
  loadStatus,
  flashMessage,
  resetForm,
}) => {
  const [data, setData] = useState({
    columns: [],
    buttons: [],
    types: [],
    isFind: false,
    status: WAIT,
  });

  const clearFilters = () => {
    resetForm(FlowTable.FORM_NAME);
    setData({ ...data, isFind: true });
  };

  const getConditions = () => {
    const selector = formValueSelector(FlowTable.FORM_NAME);
    return selector(store.getState(), 'name', 'type');
  };

  const handleFind = () => {
    setData({ ...data, isFind: true });
  };

  const openLinkInNewWindow = (item) => {
    const { id } = item;
    return `${config.urls.school}preview/${id}`;
  };

  const show = (color, message) =>
    showNotification({
      notification: <FullWideNotification color={color} message={message} />,
      position: 'full',
    });

  useEffect(() => {
    if (loadStatus === FLOWS_SUCCESS) {
      setData({ ...data, isFind: false });
    }
  }, [loadStatus]);

  useEffect(() => {
    if (flashMessage && flashMessage.length) {
      setTimeout(() => {
        show('success', flashMessage);
        flowClearFlashMessage();
      }, 1000);
    }
  }, [flashMessage]);

  useEffect(() => {
    const types = [
      {
        value: 'flow',
        label: 'Поток',
      },
      {
        value: 'instant',
        label: 'Моментальный',
      },
    ];

    const columns = [
      {
        label: '#',
        dbColumn: 'createdAt',
        listColumn: 'number',
        sortable: true,
        width: 5,
        type: DATA,
      },
      {
        label: 'Название потока',
        dbColumn: 'name',
        listColumn: 'name',
        sortable: true,
        width: 20,
        type: DATA,
      },
      {
        label: 'Тип потока',
        dbColumn: 'courseType',
        listColumn: 'courseType',
        sortable: true,
        width: 15,
        handle: (type) =>
          type === 'flow' ? <Badge color="warning">Поток</Badge> : <Badge color="primary">Моментальный</Badge>,
        type: DATA,
      },
      {
        label: 'Название курса',
        dbColumn: 'course.name',
        listColumn: 'title',
        sortable: false,
        width: 20,
        type: DATA,
      },
      {
        label: 'Старт потока',
        dbColumn: 'date.begin',
        listColumn: 'begin',
        sortable: true,
        width: 10,
        type: DATA,
      },
      {
        label: 'Окончание потока',
        dbColumn: 'date.end',
        listColumn: 'end',
        sortable: true,
        width: 10,
        type: DATA,
      },
      {
        label: 'Создан',
        dbColumn: 'createdAt',
        listColumn: 'createdAt',
        sortable: true,
        width: 10,
        type: DATA,
      },
      {
        label: 'Действия',
        dbColumn: '_id',
        listColumn: 'id',
        sortable: false,
        width: 10,
        type: ACTION,
      },
    ];

    const buttons = [
      {
        name: 'edit',
        color: 'primary',
        icon: 'edit',
        type: LINK,
        link: '/flows/',
        text: 'Редактировать',
      },
      {
        name: 'link',
        color: 'primary',
        icon: 'link',
        type: OPEN_WINDOW,
        text: 'Открыть предпросмотр',
        handle: openLinkInNewWindow,
      },
    ];

    setData({
      ...data,
      columns,
      buttons,
      types,
      status: COMPLETED,
    });

    return () => {
      flowClearState();
    };
  }, []);

  return (
    <Fragment>
      {data.status === COMPLETED && (
        <Fragment>
          <Row>
            <Col md={12} lg={12}>
              <Card>
                <CardBody>
                  <div className="card__title">
                    <h5 className="bold-text">Поиск по потокам курсов</h5>
                  </div>
                  <form className="form gap-20">
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Название потока</span>
                        <div className="form__form-group-field">
                          <Field name="name" component={renderField} type="text" placeholder="Название потока" />
                        </div>
                      </div>
                    </Col>
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Тип потока</span>
                        <div className="form__form-group-field">
                          <Field
                            name="type"
                            component={renderSelectField}
                            placeholder="Тип потока"
                            options={data.types}
                          />
                        </div>
                      </div>
                    </Col>
                  </form>
                  <ButtonToolbar className="form__button-toolbar wizard__toolbar">
                    <Button color="primary" type="button" onClick={handleFind}>
                      Найти
                    </Button>
                    <Button color="secondary" type="button" onClick={clearFilters}>
                      Очистить
                    </Button>
                  </ButtonToolbar>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={12} lg={12}>
              <DataTable
                addButton={{ to: '/flows/create', caption: 'Добавить поток' }}
                columns={data.columns}
                buttons={data.buttons}
                getConditions={getConditions}
                getList={getFlowsList}
                list={flowsList}
                pagination={pagination}
                isFind={data.isFind}
                loadListStatus={loadStatus}
                declensions={['поток', 'потока', 'потоков']}
              />
            </Col>
          </Row>
        </Fragment>
      )}
    </Fragment>
  );
};

FlowTable.FORM_NAME = 'flows_table';

FlowTable.propTypes = {
  getFlowsList: PropTypes.func.isRequired,
  flowClearState: PropTypes.func.isRequired,
  flowClearFlashMessage: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  flowsList: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  // eslint-disable-next-line react/require-default-props
  pagination: PropTypes.objectOf(PropTypes.any),
  loadStatus: PropTypes.string.isRequired,
  flashMessage: PropTypes.string.isRequired,
  resetForm: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  flowsList: state.flows.list,
  pagination: state.flows.pagination,
  loadStatus: state.flows.loadListStatus,
  flashMessage: state.flows.flash,
});

const mapDispatchToProps = (dispatch) => ({
  getFlowsList: (params) => dispatch(getFlowsListAction(params)),
  flowClearState: () => dispatch(flowClearStateAction()),
  flowClearFlashMessage: () => dispatch(flowClearFlashMessageAction()),
  resetForm: (formName) => dispatch(reset(formName)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps)(withTranslation('common')(withRouter(FlowTable)));

export default reduxForm({
  form: FlowTable.FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(withConnect);
